import {useEffect, useRef} from 'react';

/** One entry, reused by every app: only one sheet is ever open at a time. */
const TOKEN = 'app-sheet';

/**
 * Give the open app its own history entry, so the phone's back gesture parks it.
 *
 * The entry belongs to the SHELL, not to the sheet. A per-sheet effect looks
 * natural and is quietly broken: React StrictMode mounts an effect, tears it
 * down and mounts it again, and `history.back()` is asynchronous — so the
 * cleanup's `back()` landed *after* the second mount had pushed its entry and
 * re-attached its listener, and the resulting `popstate` closed the app the
 * instant it opened (a flash, then the home screen).
 *
 * The shell outlives every sheet, so here the pops we cause ourselves can simply
 * be counted and ignored, and the listener that counts them is never torn down
 * between the `back()` and the event it produces.
 */
export function useSheetHistory(open: boolean, onBack: () => void) {
  /** Pops WE asked for (dropping our own entry) and must not act on. */
  const selfPops = useRef(0);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  useEffect(() => {
    const onPopState = () => {
      if (selfPops.current > 0) {
        selfPops.current -= 1;
        return;
      }
      onBackRef.current();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    window.history.pushState({appSheet: TOKEN}, '');

    return () => {
      // Closed with a button: drop our entry so the stack does not grow one dead
      // step per app opened. Closed with `back`: it is already gone, and popping
      // again would walk the user out of the page.
      const current = window.history.state as {appSheet?: string} | null;
      if (current?.appSheet !== TOKEN) return;
      selfPops.current += 1;
      window.history.back();
    };
  }, [open]);
}
